import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Fade,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  TextField,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import apiFetch from "../../utils/apiFetch";

const SCHOOL_YEAR_STORAGE_KEY = "current_school_year";

// Clears the stored school year then reloads on the v2 chrome route, matching the
// legacy school switcher behavior. Selecting the current school is a no-op.
function switchToSchool(school, currentSchoolSlug) {
  if (school.slug === currentSchoolSlug) return;
  try {
    window.localStorage.removeItem(SCHOOL_YEAR_STORAGE_KEY);
  } catch (e) {
    // ignore storage errors (private mode, etc.)
  }
  window.location.replace(`/v2/school/${school.slug}`);
}

// The inline switcher panel: a client-side search field over the already-fetched
// schools plus the filterable list. It mounts only while the switcher is open, so
// the query resets and the search field re-focuses on each open for free.
function SchoolSwitcherPanel({ schools, currentSchoolSlug }) {
  const [query, setQuery] = useState("");
  // Index of the keyboard-highlighted option. Focus stays in the search field
  // (combobox pattern); arrow keys move this highlight and Enter selects it.
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const itemRefs = useRef([]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return schools;
    return schools.filter((school) =>
      school.name.toLowerCase().includes(needle)
    );
  }, [schools, query]);

  // Reset the highlight to the top whenever the filtered set changes, so it never
  // points past the end of the list.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  // Keep the highlighted row in view as the user arrows through a long list.
  useEffect(() => {
    itemRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const handleKeyDown = (e) => {
    if (filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const school = filtered[highlightedIndex];
      if (school) switchToSchool(school, currentSchoolSlug);
    }
  };

  const activeOptionId =
    filtered[highlightedIndex] &&
    `school-option-${filtered[highlightedIndex].id}`;

  return (
    <Box>
      <TextField
        autoFocus
        fullWidth
        size="small"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search schools"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "#00000080" }} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setQuery("")}
                  edge="end"
                >
                  <ClearIcon fontSize="small" sx={{ color: "#00000080" }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
          htmlInput: {
            "aria-label": "Search schools",
            role: "combobox",
            "aria-expanded": true,
            "aria-controls": "school-switcher-list",
            "aria-activedescendant": activeOptionId || undefined,
          },
        }}
        sx={{ mb: 1 }}
      />
      {filtered.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: "#00000099", px: 1, py: 1 }}>
          No schools found
        </Typography>
      ) : (
        <List id="school-switcher-list" role="listbox" sx={{ p: 0 }}>
          {filtered.map((school, index) => {
            const isActive = school.slug === currentSchoolSlug;
            const isHighlighted = index === highlightedIndex;
            const label = `${school.name}${
              school.is_default_school ? " (Default)" : ""
            }`;
            return (
              <ListItemButton
                key={school.id}
                id={`school-option-${school.id}`}
                role="option"
                aria-selected={isHighlighted}
                selected={isActive}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                onClick={() => switchToSchool(school, currentSchoolSlug)}
                onMouseEnter={() => setHighlightedIndex(index)}
                aria-label={`Switch to ${school.name}`}
                sx={{
                  borderRadius: 1,
                  py: 0.75,
                  backgroundColor: isHighlighted
                    ? "rgba(0, 0, 0, 0.04)"
                    : undefined,
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {isActive && (
                    <CheckIcon fontSize="small" sx={{ color: "#00000080" }} />
                  )}
                </ListItemIcon>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: isActive ? 600 : 400,
                    color: "#000000",
                  }}
                >
                  {label}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
}

// School switcher in the nav drawer/sidebar. Collapsed, it shows the current school
// name plus a chevron; the parent (SchoolNavContents) fades the rest of the menu out
// and reveals the inline panel below when `open`. The default school is suffixed
// "(Default)", the active school is checkmarked. Staff with a single school have
// nothing to switch to, so the whole section is hidden.
export default function SchoolSwitcherList({
  staff,
  currentSchoolSlug,
  currentSchoolName,
  open,
  onToggle,
}) {
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    if (!staff?.id) return undefined;
    let cancelled = false;
    apiFetch(`/api/staffs/${staff.id}/schools`)
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (cancelled) return;
        setSchools(payload?.data?.schools ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [staff?.id]);

  // A staff with a single school has nothing to switch to - hide the section.
  if (!staff?.id || schools.length <= 1) return null;

  return (
    <Box>
      <ListItemButton
        role="button"
        aria-label="Switch school"
        aria-expanded={open}
        onClick={onToggle}
        sx={{ borderRadius: 1, py: 0.75 }}
      >
        <Typography
          sx={{
            flex: 1,
            fontSize: 15,
            fontWeight: 600,
            color: "#000000",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {currentSchoolName}
        </Typography>
        <ExpandMoreIcon
          sx={{
            color: "#00000080",
            transition: "transform 150ms",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </ListItemButton>

      <Fade
        in={open}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 150, exit: 0 }}
      >
        <Box sx={{ mt: 0.5 }}>
          <SchoolSwitcherPanel
            schools={schools}
            currentSchoolSlug={currentSchoolSlug}
          />
        </Box>
      </Fade>
    </Box>
  );
}
